import React, { useEffect, useMemo, useRef, useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { useChatsUIContext } from "../context/ChatsContext";
import { Tab } from "../hooks/useTabs";
import { SelectedTabType } from "../types";

const transition = {
  type: "tween",
  ease: "easeOut",
  duration: 0.25,
};

type Props = {
  selectedTabIndex: number;
  tabs: Tab[];
  setSelectedTab: (input: [number, number]) => void;
};

const FramerTabHeader = ({
  tabs,
  selectedTabIndex,
  setSelectedTab,
}: Props): JSX.Element => {
  const [buttonRefs, setButtonRefs] = useState<Array<HTMLButtonElement | null>>(
    []
  );

  const chatsContext = useChatsUIContext();
  const chatsUIProps = useMemo(() => {
    return {
      selectedTab: chatsContext?.selectedTab,
      setSelectedTab: chatsContext?.setSelectedTab,
    };
  }, [chatsContext?.selectedTab, chatsContext?.setSelectedTab]);

  useEffect(() => {
    setButtonRefs((prev) => prev.slice(0, tabs.length));
  }, [tabs.length]);

  const navRef = useRef<HTMLDivElement>(null);
  const navRect = navRef.current?.getBoundingClientRect();

  const selectedRect = buttonRefs[selectedTabIndex]?.getBoundingClientRect();

  const [hoveredTabIndex, setHoveredTabIndex] = useState<number | null>(null);
  const hoveredRect =
    buttonRefs[hoveredTabIndex ?? -1]?.getBoundingClientRect();
  const handleSelectTab = (tab: Tab, i: number) => {
    setSelectedTab([i, i > selectedTabIndex ? 1 : -1]);
    chatsUIProps?.setSelectedTab?.(tab.id as SelectedTabType);
  };

  useEffect(() => {
    const index = tabs?.findIndex(
      (tab) => tab.id === chatsUIProps?.selectedTab
    );
    setSelectedTab([index, 1]);
  }, [chatsUIProps?.selectedTab, setSelectedTab, tabs]);

  return (
    <nav
      ref={navRef}
      className="d-flex align-items-center position-relative mx-1"
      style={{
        zIndex: 0,
        flexShrink: "0",
        overflowX: "auto",
      }}
      onPointerLeave={(e) => setHoveredTabIndex(null)}
    >
      {tabs.map((item: Tab, i: number) => {
        const isActive = hoveredTabIndex === i || selectedTabIndex === i;

        return (
          <motion.button
            key={i}
            className={clsx(
              "btn d-flex align-items-center px-3 bg-transparent cursor-pointer user-select-none mb-2",
              {
                "text-white": isActive,
              }
            )}
            style={{
              zIndex: 20,
              fontSize: "1rem",
              height: "2.5rem",
              borderRadius: "0.375rem",
              transition: "color 0.3s",
            }}
            ref={(el) => (buttonRefs[i] = el)}
            onPointerEnter={() => {
              setHoveredTabIndex(i);
            }}
            onFocus={() => {
              setHoveredTabIndex(i);
            }}
            onClick={() => {
              handleSelectTab(item, i);
            }}
          >
            {item.label}
          </motion.button>
        );
      })}

      <AnimatePresence>
        {hoveredRect && navRect && (
          <motion.div
            key={"hover"}
            className="position-absolute p-1 mb-1 top-0 start-0 rounded"
            style={{
              zIndex: 10,
              backgroundColor: "#c8bca75e",
            }}
            initial={{
              x: hoveredRect.left - navRect.left,
              y: hoveredRect.top - navRect.top,
              width: hoveredRect.width,
              height: hoveredRect.height,
              opacity: 0,
            }}
            animate={{
              x: hoveredRect.left - navRect.left,
              y: hoveredRect.top - navRect.top,
              width: hoveredRect.width,
              height: hoveredRect.height,
              opacity: 1,
            }}
            exit={{
              x: hoveredRect.left - navRect.left,
              y: hoveredRect.top - navRect.top,
              width: hoveredRect.width,
              height: hoveredRect.height,
              opacity: 0,
            }}
            transition={transition}
          />
        )}
      </AnimatePresence>

      {selectedRect && navRect && (
        <motion.div
          className="position-absolute"
          style={{
            zIndex: 10,
            bottom: 0,
            left: "0.125rem",
            height: "4px",
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
            backgroundColor: "#E59B3C",
          }}
          initial={false}
          animate={{
            width: selectedRect.width * 0.8,
            x: `calc(${selectedRect.left - navRect.left}px + 10%)`,
            opacity: 1,
          }}
          transition={transition}
        />
      )}
    </nav>
  );
};

export { FramerTabHeader };
